/**
 * Protocolo entre o script do site do cliente (`embed.ts`) e o app dentro do iframe.
 * Prefixo + JSON, como o `gap-10 §16.4` define — o prefixo é o que permite ignorar sem custo
 * as mensagens que outras bibliotecas da página também mandam pelo `postMessage`.
 */
export const PROTOCOL_PREFIX = 'chat.agente-widget:';

/** Parâmetros que o embed passa ao iframe pela URL, e que o app lê no arranque. */
export const WEBSITE_TOKEN_PARAM = 'websiteToken';
export const HOST_ORIGIN_PARAM = 'hostOrigin';

export interface VisitorIdentity {
  identifier?: string;
  identifierHash?: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  postalCode?: string;
  customAttributes?: Record<string, unknown>;
}

export type HostMessage =
  | { event: 'set-user'; identity: VisitorIdentity }
  | { event: 'set-custom-attributes'; customAttributes: Record<string, unknown> }
  | { event: 'visibility'; open: boolean };

export type AppMessage =
  | { event: 'loaded'; widgetColor: string }
  | { event: 'unread'; count: number }
  | { event: 'close' };

const HOST_EVENTS: readonly string[] = ['set-user', 'set-custom-attributes', 'visibility'];

const APP_EVENTS: readonly string[] = ['loaded', 'unread', 'close'];

export function encodeMessage(message: HostMessage | AppMessage): string {
  return `${PROTOCOL_PREFIX}${JSON.stringify(message)}`;
}

function parseEnvelope(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'string' || !raw.startsWith(PROTOCOL_PREFIX)) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw.slice(PROTOCOL_PREFIX.length));
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function decode<T>(raw: unknown, events: readonly string[]): T | null {
  const envelope = parseEnvelope(raw);

  if (envelope === null) {
    return null;
  }

  const event = envelope['event'];
  return typeof event === 'string' && events.includes(event) ? (envelope as T) : null;
}

export function decodeHostMessage(raw: unknown): HostMessage | null {
  return decode<HostMessage>(raw, HOST_EVENTS);
}

export function decodeAppMessage(raw: unknown): AppMessage | null {
  return decode<AppMessage>(raw, APP_EVENTS);
}
